import { MouseEvent, useCallback, useEffect, useRef, useState } from 'react';

const DEFAULT_TARGET_SELECTOR = 'input, textarea';

export const useTextTruncation = (targetSelector = DEFAULT_TARGET_SELECTOR) => {
  const [tooltipContent, setTooltipContent] = useState<string>('');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isHoveringRef = useRef(false);

  const onMouseEnter = useCallback(
    (e: MouseEvent<HTMLElement>) => {
      const currentTarget = e.currentTarget;
      isHoveringRef.current = true;

      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      timeoutRef.current = setTimeout(() => {
        if (!isHoveringRef.current) return;

        const element =
          currentTarget instanceof HTMLInputElement ||
          currentTarget instanceof HTMLTextAreaElement
            ? currentTarget
            : currentTarget.querySelector<HTMLElement>(targetSelector);

        if (!element) return;

        const style = window.getComputedStyle(element);
        const paddingLeft = parseFloat(style.paddingLeft) || 0;
        const paddingRight = parseFloat(style.paddingRight) || 0;

        const availableSpace = element.clientWidth - paddingLeft - paddingRight;

        const textWidth = element.scrollWidth - paddingLeft - paddingRight;

        const isOverflowing = textWidth > availableSpace + 1;

        if (isOverflowing) {
          const content =
            element instanceof HTMLInputElement ||
            element instanceof HTMLTextAreaElement
              ? element.value
              : element.textContent;

          setTooltipContent(content?.trim() ?? '');
        }
      }, 50);
    },
    [targetSelector]
  );

  const onMouseLeave = useCallback(() => {
    isHoveringRef.current = false;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = null;
    setTooltipContent('');
  }, []);

  useEffect(
    () => () => {
      isHoveringRef.current = false;
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    },
    []
  );

  return {
    tooltipContent,
    isTruncated: !!tooltipContent,
    bindTruncation: {
      onMouseEnter,
      onMouseLeave,
    },
  };
};
